'use client';

import type {
  ChangeEvent,
  DragEvent,
  KeyboardEvent,
  MouseEvent,
  RefObject,
} from 'react';
import { useTranslations } from 'next-intl';
import { Box, HStack } from '@terra/ui-styled-system';
import {
  Badge,
  BadgeHttp,
  IconButton,
  Input,
  LabelDefault,
  Menu,
  Tooltip,
} from '@terra/ui-design-system';
import {
  Tick01Icon,
  Folder01Icon,
  DragDropVerticalIcon,
  MoreHorizontalIcon,
  Edit03Icon,
  Cancel01Icon,
} from '@terra/ui-icons';
import type { ReplayCollection } from '../../types/replay';
import type { ReplaySession } from '../../store/replaySessionStore';
import { REPLAY_COLLECTION_DRAG_TYPE } from '../../store/replay-session-order';

type ReplaySessionRowProps = {
  session: ReplaySession;
  collections: ReplayCollection[];
  isEditing: boolean;
  editingTitle: string;
  isActive: boolean;
  isHovered: boolean;
  isDragged: boolean;
  isDropTarget: boolean;
  sessionEditInputRef: RefObject<HTMLInputElement | null>;
  onHoverChange: (sessionId: string | null) => void;
  onDragStart: (sessionId: string) => void;
  onDragEnd: () => void;
  onDropOnSession: (beforeSessionId: string) => void;
  onDropCollection: (collectionId: string) => void;
  onDropTargetChange: (sessionId: string | null) => void;
  onSelect: () => void;
  onClose: () => void;
  onEditingTitleChange: (title: string) => void;
  onSaveTitle: () => void;
  onCancelEdit: () => void;
  onStartRename: () => void;
  onMoveToCollection: (collectionId: string) => void;
};

export function ReplaySessionRow({
  session,
  collections,
  isEditing,
  editingTitle,
  isActive,
  isHovered,
  isDragged,
  isDropTarget,
  sessionEditInputRef,
  onHoverChange,
  onDragStart,
  onDragEnd,
  onDropOnSession,
  onDropCollection,
  onDropTargetChange,
  onSelect,
  onClose,
  onEditingTitleChange,
  onSaveTitle,
  onCancelEdit,
  onStartRename,
  onMoveToCollection,
}: ReplaySessionRowProps) {
  const tCollections = useTranslations('replay.collections');

  const handleMouseEnter = () => {
    onHoverChange(session.id);
  };

  const handleMouseLeave = () => {
    onHoverChange(null);
  };

  const handleDragStart = (event: DragEvent<HTMLDivElement>) => {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', session.id);
    onDragStart(session.id);
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';

    if (
      Array.from(event.dataTransfer.types).includes(REPLAY_COLLECTION_DRAG_TYPE)
    ) {
      return;
    }

    event.stopPropagation();
    onDropTargetChange(session.id);
  };

  const handleDragLeave = () => {
    onDropTargetChange(null);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();

    const draggedCollectionId = event.dataTransfer.getData(
      REPLAY_COLLECTION_DRAG_TYPE,
    );

    if (draggedCollectionId) {
      onDropCollection(draggedCollectionId);

      return;
    }

    event.stopPropagation();
    onDropOnSession(session.id);
  };

  const handleClick = () => {
    if (!isEditing) {
      onSelect();
    }
  };

  const handleMouseDown = (event: MouseEvent<HTMLDivElement>) => {
    if (event.button === 1) {
      event.preventDefault();
      event.stopPropagation();
      onClose();
    }
  };

  const handleTitleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onEditingTitleChange(event.target.value);
  };

  const handleTitleClick = (event: MouseEvent) => {
    event.stopPropagation();
  };

  const handleTitleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onSaveTitle();
    }

    if (event.key === 'Escape') {
      onCancelEdit();
    }
  };

  const handleTitleDoubleClick = (event: MouseEvent) => {
    event.stopPropagation();
    onStartRename();
  };

  const handleSaveClick = (event: MouseEvent) => {
    event.stopPropagation();
    onSaveTitle();
  };

  const handleMenuTriggerClick = (event: MouseEvent) => {
    event.stopPropagation();
  };

  const handleCloseClick = (event: MouseEvent) => {
    event.stopPropagation();
    onClose();
  };

  return (
    <Tooltip
      title={`${session.request.method} ${session.title}`}
      maxW="700px"
      allowOverflow
      positioning={{ placement: 'bottom', strategy: 'fixed' }}
    >
      <HStack
        gap="1"
        px="1.5"
        py="1"
        w="full"
        bg={isDropTarget ? 'bg.muted' : isActive ? 'bg.default' : 'transparent'}
        borderLeftWidth="2px"
        borderLeftColor={isActive ? 'border.accent' : 'transparent'}
        cursor="pointer"
        minW="0"
        draggable={!isEditing}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onDragStart={handleDragStart}
        onDragEnd={onDragEnd}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
        onMouseDown={handleMouseDown}
        _hover={{ bg: isActive ? 'bg.default' : 'bg.muted' }}
        opacity={isDragged ? 0.5 : 1}
      >
        <Box
          color="fg.muted"
          display="flex"
          alignItems="center"
          cursor="grab"
          flexShrink={0}
          w="3"
        >
          <DragDropVerticalIcon size={10} />
        </Box>
        <Box flexShrink={0} w="14" display="flex">
          <BadgeHttp method={session.request.method} size="sm">
            {session.request.method}
          </BadgeHttp>
        </Box>
        {isEditing ? (
          <Input
            ref={sessionEditInputRef}
            size="xs"
            value={editingTitle}
            onChange={handleTitleChange}
            onClick={handleTitleClick}
            onBlur={onSaveTitle}
            onKeyDown={handleTitleKeyDown}
            flex="1"
            minW="0"
            h="22px"
          />
        ) : (
          <LabelDefault
            truncate
            flex="1"
            minW="0"
            color={isActive ? 'fg.default' : 'fg.muted'}
            fontWeight={isActive ? 'medium' : 'normal'}
            onDoubleClick={handleTitleDoubleClick}
          >
            {session.title}
          </LabelDefault>
        )}
        <Box flexShrink={0} w="6" display="flex" justifyContent="center">
          {session.responseHistory.length > 0 && !isEditing ? (
            <Badge size="sm">{session.responseHistory.length}</Badge>
          ) : null}
        </Box>
        {isEditing ? (
          <IconButton
            variant="link"
            size="xs"
            aria-label={tCollections('saveSessionTitle')}
            minW="auto"
            h="auto"
            p="1"
            onClick={handleSaveClick}
          >
            <Tick01Icon size={10} />
          </IconButton>
        ) : (
          <HStack gap="0.5" flexShrink={0} w="12" justifyContent="flex-end">
            <Menu.Root positioning={{ strategy: 'fixed' }}>
              <Menu.Trigger asChild>
                <IconButton
                  variant="link"
                  size="xs"
                  aria-label={tCollections('sessionMenu')}
                  minW="auto"
                  h="auto"
                  p="1"
                  opacity={isHovered || isActive ? 1 : 0}
                  visibility={isHovered || isActive ? 'visible' : 'hidden'}
                  onClick={handleMenuTriggerClick}
                >
                  <MoreHorizontalIcon size={10} />
                </IconButton>
              </Menu.Trigger>
              <Menu.Positioner>
                <Menu.Content minW="150px" maxH="260px" overflowY="auto" py="1">
                  {collections
                    .filter(
                      (collection) => collection.id !== session.collectionId,
                    )
                    .map((collection) => (
                      <Menu.Item
                        key={collection.id}
                        value={`move-${collection.id}`}
                        fontSize="xs"
                        onClick={() => onMoveToCollection(collection.id)}
                        py="1"
                        px="2"
                      >
                        <HStack gap="1.5">
                          <Folder01Icon size={12} />
                          <LabelDefault>
                            {tCollections('moveToCollection', {
                              name: collection.name,
                            })}
                          </LabelDefault>
                        </HStack>
                      </Menu.Item>
                    ))}
                  <Menu.Item
                    value="rename"
                    fontSize="xs"
                    onClick={onStartRename}
                    py="1"
                    px="2"
                  >
                    <HStack gap="1.5">
                      <Edit03Icon size={12} />
                      <LabelDefault>{tCollections('rename')}</LabelDefault>
                    </HStack>
                  </Menu.Item>
                </Menu.Content>
              </Menu.Positioner>
            </Menu.Root>
            <IconButton
              variant="link"
              size="xs"
              aria-label={tCollections('closeSession')}
              minW="auto"
              h="auto"
              p="1"
              opacity={isHovered || isActive ? 1 : 0}
              visibility={isHovered || isActive ? 'visible' : 'hidden'}
              onClick={handleCloseClick}
            >
              <Cancel01Icon size={10} />
            </IconButton>
          </HStack>
        )}
      </HStack>
    </Tooltip>
  );
}
